/**
 * RTWO Event Bus — Dead Letter Queue Service
 */

import { EVENT_BUS_SERVICE_IDS } from '../domain-models';
import type { DeadLetterId, EventId, RTWODeadLetterQueue } from '../domain-models';
import type { DeadLetterResult, IDeadLetterQueueService } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import type { EngineSourceAdapterRegistry } from '../registry/adapter-registries';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class DeadLetterQueueService extends BaseEBusService implements IDeadLetterQueueService {
  readonly serviceId = EVENT_BUS_SERVICE_IDS.DEAD_LETTER_QUEUE;
  readonly serviceName = 'Dead Letter Queue';
  readonly description = 'Holds events that exhausted retry attempts. Dead letter queue is empty.';
  readonly integrations = ['Retry Manager', 'Delivery Engine', 'Event Registry'];
  readonly requiresRealEventSources = true;

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories,
    private readonly engineSourceRegistry: EngineSourceAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'structure_ready';
  }

  async moveToDeadLetter(_eventId: EventId, _reason: string): Promise<DeadLetterResult> {
    return {
      success: false,
      queueId: null,
      error: 'Dead Letter Queue Empty — No events have been dead-lettered. Queue is empty.',
      isPlaceholder: true,
      source: 'Event Bus — Dead Letter Queue Empty',
    };
  }

  getAllDeadLetters(): RTWODeadLetterQueue[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.deadLetters.findAll();
  }

  getDeadLetter(queueId: DeadLetterId): RTWODeadLetterQueue | null {
    if (!this.engineSourceRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.deadLetters.findById(queueId);
  }

  getDeadLetterCount(): 0 {
    return 0;
  }
}
